import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Stack } from '@mui/material';

import { FCheckbox, FHInputField } from '../../../base';
import { Themes } from '../../../../constants';
import { DISCHARGE_REASONS_LABELS } from './constants';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import FTypography from '../../../base/FTypography';
import { dischargeTransitionActions } from '../../../../store/treatment-plan/dischargeTransition.slice';

import type { ReasonsTransformed } from './index';
import type { IDischargeCriteria, IDischargeTransitionReason } from '../../../../utils/types';
import type { ChangeEvent } from 'react';

interface ReasonsProps {
  reasons: ReasonsTransformed;
  dischargeCriteria: IDischargeCriteria;
  onUpdateCheckField: (transitionReason: IDischargeTransitionReason, checked: boolean) => void;
  onUpdateInputField: (transitionReason: IDischargeTransitionReason, value: string) => void;
  reasonsPendingState: any;
}

function Reasons({
  reasons,
  dischargeCriteria,
  onUpdateCheckField,
  onUpdateInputField,
  reasonsPendingState,
}: ReasonsProps) {
  const dispatch = useDispatch();
  const isEditable = useTreatmentPlanEditable();
  const [otherField, setOtherField] = useState<IDischargeTransitionReason | null>(
    reasons?.['Other'] ?? null,
  );
  const [isOtherFieldChecked, setIsOtherFieldChecked] = useState<boolean>(false);
  const [isOtherFieldError, setIsOtherFieldError] = useState<boolean>(false);

  useEffect(() => {
    if (!reasons || !dischargeCriteria?.id) return;
    dispatch(
      dischargeTransitionActions.updateState({
        key: 'reasonsErrorCount',
        data: Object.entries(reasons).length > 0 ? 0 : 1,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasons, dischargeCriteria]);

  useEffect(() => {
    if (!reasons) return;
    setIsOtherFieldChecked(Boolean(reasons?.['Other']));
    setOtherField(reasons?.['Other']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasons?.['Other']]);

  useEffect(() => {
    if (!otherField) {
      setIsOtherFieldError(Boolean(isOtherFieldChecked));
      return;
    }
    setIsOtherFieldError(
      isOtherFieldChecked && (!otherField?.description || otherField?.description === ''),
    );
  }, [isOtherFieldChecked, otherField]);

  const handleOtherFieldUpdate = (key: string, value: string) => {
    setOtherField(() => ({
      ...reasons?.['Other'],
      ...otherField,
      [key]: value,
      label: 'Other',
    }));
  };

  return (
    <Stack>
      <Box mb={2}>
        <FTypography
          error={Boolean(dischargeCriteria?.id) && Object.entries(reasons).length === 0}
          fontWeight={'bold'}
          textAlign="left"
        >
          Reasons to decrease the number of hours of therapy per week to full discharge may include:
        </FTypography>
      </Box>
      <Stack spacing={2}>
        {DISCHARGE_REASONS_LABELS.map((REASON_LABEL) => (
          <FCheckbox
            data-testid={`${REASON_LABEL.text.replace(/\s+/g, '_')}_checkbox`}
            key={REASON_LABEL.key}
            label={REASON_LABEL.text}
            colorTheme={Themes.Primary}
            checked={reasons?.[REASON_LABEL.key]?.id !== undefined}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              onUpdateCheckField(
                {
                  ...reasons?.[REASON_LABEL.key],
                  label: REASON_LABEL.text,
                  description: null,
                },
                e.target.checked,
              );
            }}
            disabled={
              !isEditable || !dischargeCriteria?.id || reasonsPendingState[REASON_LABEL.text]
            }
          />
        ))}
        <Box sx={{ display: 'flex' }}>
          <FCheckbox
            data-testid="otherCheckbox"
            label="Other:"
            colorTheme={Themes.Primary}
            checked={isOtherFieldChecked}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              if (e.target.checked) {
                // when this happens, we are only enabing the corresponding text input
                setIsOtherFieldChecked(e.target.checked);
              } else {
                // need to delete reason if this happens
                setIsOtherFieldChecked(e.target.checked);
                onUpdateCheckField(
                  {
                    ...reasons?.['Other'],
                    label: 'Other',
                    description: otherField?.description ?? '',
                  },
                  e.target.checked,
                );
              }
            }}
            disabled={!isEditable || !dischargeCriteria?.id || reasonsPendingState['Other']}
          />
          <FHInputField
            data-testid="otherTextField"
            fullWidth
            disabled={
              !isEditable ||
              !dischargeCriteria?.id ||
              !isOtherFieldChecked ||
              reasonsPendingState['Other']
            }
            colorTheme={Themes.Primary}
            value={otherField?.description || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handleOtherFieldUpdate('description', e.target.value);
            }}
            onBlur={(e) => {
              onUpdateInputField(
                {
                  ...reasons?.['Other'],
                  label: 'Other',
                  description: otherField?.['description'] || '',
                },
                e.target.value,
              );
            }}
            error={isOtherFieldError}
            inputHeight={40}
          />
        </Box>
      </Stack>
    </Stack>
  );
}

export default Reasons;
